'use client';

import type { LeadEventWithStatusDto } from '@up2stay/up2promo-api-client';
import { Button, cn } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';

export const LEAD_EVENT_CAPTURE_NONE = '__none__';

type LeadEventCapturePickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  events: LeadEventWithStatusDto[];
  value: string;
  onChange: (eventId: string) => void;
  onConfirm: () => void;
  labels: {
    title: string;
    none: string;
    confirm: string;
    cancel: string;
  };
};

/** E4 — pick which overlapping active event a new lead belongs to. */
export function LeadEventCapturePicker({
  open,
  onOpenChange,
  events,
  value,
  onChange,
  onConfirm,
  labels,
}: LeadEventCapturePickerProps) {
  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{labels.title}</ResponsiveDialogTitle>
        </ResponsiveDialogHeader>
        <div className="flex flex-col gap-0 overflow-hidden rounded-[12px] border border-[color:var(--surface-border)]">
          {events.map((ev) => {
            const selected = value === ev.id;
            const location = ev.location?.trim() || '';
            return (
              <button
                key={ev.id}
                type="button"
                onClick={() => onChange(ev.id)}
                className={cn(
                  'flex min-h-12 w-full items-center justify-between gap-3 border-b border-[color:var(--surface-border)] px-3.5 py-3 text-left last:border-b-0',
                  selected &&
                    'bg-[color:var(--ghost-accent-bg)] ring-1 ring-inset ring-primary',
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={cn(
                      'inline-flex size-4 shrink-0 items-center justify-center rounded-full border',
                      selected
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground/40',
                    )}
                    aria-hidden
                  >
                    {selected ? (
                      <span className="size-1.5 rounded-full bg-primary-foreground" />
                    ) : null}
                  </span>
                  <span className="truncate text-[13.5px] font-semibold">
                    {ev.name}
                  </span>
                </span>
                {location ? (
                  <span className="shrink-0 text-[12.5px] text-muted-foreground">
                    {location}
                  </span>
                ) : null}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => onChange(LEAD_EVENT_CAPTURE_NONE)}
            className={cn(
              'flex min-h-12 w-full items-center gap-2.5 px-3.5 py-3 text-left',
              value === LEAD_EVENT_CAPTURE_NONE &&
                'bg-[color:var(--ghost-accent-bg)] ring-1 ring-inset ring-primary',
            )}
          >
            <span
              className={cn(
                'inline-flex size-4 shrink-0 items-center justify-center rounded-full border',
                value === LEAD_EVENT_CAPTURE_NONE
                  ? 'border-primary bg-primary'
                  : 'border-muted-foreground/40',
              )}
              aria-hidden
            >
              {value === LEAD_EVENT_CAPTURE_NONE ? (
                <span className="size-1.5 rounded-full bg-primary-foreground" />
              ) : null}
            </span>
            <span className="text-[13.5px] font-semibold">{labels.none}</span>
          </button>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {labels.cancel}
          </Button>
          <Button type="button" onClick={onConfirm}>
            {labels.confirm}
          </Button>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
